import React, { useCallback, useState } from "react"
import MuiAutocomplete from "@mui/material/Autocomplete"
import TextField from "@mui/material/TextField"
import Box from "@mui/material/Box"
import { filter, isEqual, map, some } from "lodash"
import { Chip, Tooltip } from "@mui/material"
import CheckIcon from "@mui/icons-material/Check"

interface AutocompleteFilterProps<T> {
  title: string
  field: string
  value?: any
  optionKey: string
  collection: Array<T>
  optionLabel: (item: T) => string
  optionValue: (item: T) => any
  isDisabled?: (item: T) => boolean
  onChange: ({ field, value }) => void
}

export default function AutocompleteMulti<T extends any>(props: AutocompleteFilterProps<T>) {
  const {
    title,
    value,
    collection,
    optionLabel,
    isDisabled,
    optionKey,
    field,
    onChange,
    optionValue,
  } = props
  const [selectedValue, setSelectedValue] = useState<T[]>(value || [])

  const handleChange = (_: any, newValue: T[] | null, action: string, option) => {
    const updatedValue = newValue || []
    setSelectedValue(updatedValue)

    const result = newValue?.map(optionValue)

    onChange({
      field,
      value: result?.length ? result : undefined,
    })
  }

  const showValueToottip = () => {
    if (selectedValue?.length > 1) {
      return selectedValue?.[0]?.[optionKey]
        ? selectedValue.map((value) => value?.[optionKey]).join(",")
        : selectedValue.join(",")
    }

    return ""
  }

  const truncateLabel = (label: string, maxLength: number) => {
    if (value?.length) {
      return label.length > maxLength ? label.slice(0, maxLength) + "…" : label
    }
    return label
  }

  return (
    <Tooltip title={showValueToottip()} placement="top">
      <Box>
        <MuiAutocomplete
          id=""
          multiple
          onChange={handleChange}
          options={collection}
          getOptionLabel={optionLabel}
          getOptionDisabled={isDisabled}
          defaultValue={[]}
          renderInput={(params) => {
            return <TextField {...params} label={title} size="small" variant="outlined" />
          }}
          renderTags={(value: readonly T[], getTagProps) => {
            const maxDisplay = 1

            const displayedItems = value.slice(0, maxDisplay)

            const remainingCount = value.length - maxDisplay
            return (
              <>
                {displayedItems.map((option: T, index: number) => {
                  const { key, ...tagProps } = getTagProps({
                    index,
                  })
                  return (
                    <Chip
                      key={key}
                      variant="outlined"
                      label={truncateLabel(optionLabel(option), 5)}
                      size="small"
                      {...tagProps}
                    />
                  )
                })}
                {remainingCount > 0 && (
                  <Chip
                    variant="outlined"
                    label={`+${remainingCount} more`}
                    size="small"
                    disabled
                  />
                )}
              </>
            )
          }}
          renderOption={(props, option, { selected }) => {
            return (
              <li {...props}>
                <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                  <Box sx={{ flexGrow: 1 }}>{optionLabel(option)}</Box>
                  {selected && <CheckIcon fontSize="small" color="primary" />}
                </Box>
              </li>
            )
          }}
          fullWidth
        />
      </Box>
    </Tooltip>
  )
}
